/**
 * A client's messages on their record, with a way out of the small box.
 *
 * Her video: the thread was "crammed in this little box" between the tabs
 * and the composer, so on a laptop only one or two bubbles showed. The tab
 * now fills the height it has, and Expand opens the same conversation full
 * screen — history above, composer docked at the bottom — with Collapse (or
 * Esc) to get back to the record.
 *
 * Only one copy of the conversation is ever mounted: while expanded, the tab
 * shows a placeholder, so there aren't two pollers or two drafts.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Maximize2, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui";
import { formatPhone, fullName } from "@/lib/format";
import type { Contact } from "@/lib/types";
import { ContactInboxPanel } from "./ContactInboxPanel";

export function ExpandableMessages({ contact }: { contact: Contact }) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Not while typing: collapsing would throw the draft away.
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "TEXTAREA" || t.tagName === "INPUT" || t.isContentEditable)) return;
      setExpanded(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [expanded]);

  if (expanded) {
    const name = fullName(contact) || "Client";
    const reach = [contact.phone ? formatPhone(contact.phone) : null, contact.email].filter(Boolean).join(" · ");
    return (
      <>
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-zinc-800 px-4 py-6 text-sm text-zinc-500">
          Messages are open in the large view.
          <Button size="sm" variant="outline" leftIcon={<Minimize2 className="h-3.5 w-3.5" />} onClick={() => setExpanded(false)}>
            Collapse
          </Button>
        </div>
        {createPortal(
          <div
            className="fixed inset-0 z-50 flex flex-col bg-zinc-950"
            role="dialog"
            aria-modal="true"
            aria-label={`Messages with ${name}`}
          >
            <header className="flex shrink-0 items-center gap-3 border-b border-zinc-800/70 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-zinc-100">{name}</p>
                {reach && <p className="truncate text-xs text-zinc-500">{reach}</p>}
              </div>
              <Button size="sm" variant="outline" leftIcon={<Minimize2 className="h-3.5 w-3.5" />} onClick={() => setExpanded(false)}>
                Collapse
              </Button>
            </header>
            <div className="mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col">
              <ContactInboxPanel contact={contact} bordered={false} />
            </div>
          </div>,
          document.body,
        )}
      </>
    );
  }

  return (
    <div className="relative flex h-full min-h-[28rem] flex-col">
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="absolute right-2 top-1.5 z-10 inline-flex h-7 items-center gap-1 rounded-md bg-zinc-950/80 px-2 text-xs font-medium text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
        title="Open the conversation full screen"
      >
        <Maximize2 className="h-3.5 w-3.5" />
        Expand
      </button>
      <ContactInboxPanel contact={contact} />
    </div>
  );
}
